import React, { useState, ReactNode, useMemo } from 'react'
import styled from 'styled-typed'
import { MessageKind } from 'uiComponents/messages'
import { Text, StatusType } from 'uiComponents/typography'
import { UploadInput } from 'uiComponents/input/upload'
import { DownloadElement } from 'uiComponents/input/downloadComponent'
import { UploadDetails } from 'venue/bookingCodes/bookingCodesService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faCheckCircle, faUndoAlt } from '@fortawesome/free-solid-svg-icons'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import { faFileTimes } from '@fortawesome/pro-regular-svg-icons'
import { Col, Row } from 'uiComponents/flex'
import { addSeparators, conviousEmail } from 'utils'
import { withFeatures } from 'features'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { Checkbox } from 'uiComponents/input/checkbox'
import { BookingCodesService, UploadType, UsedBarcodesCount } from './bookingCodesService'
import { delay } from 'utils'
import DownloadLink from 'uiComponents/downloadLink'
import { LoginService } from 'http/loginService'
import { ReactTable, TableColumn } from 'uiComponents/table/reactTable'
import { Sorting } from 'uiComponents/table'
import { DateFormats, formatISOString, parseISODate } from 'utils/dates'
import { useAppSelector } from 'store/hooks'
import i18n, { useTranslation, Trans } from 'translations'

const FileNameAsLink = styled.span`
    color: ${(props) => props.theme.colors.boyBlue};
    text-decoration: underline;
    cursor: pointer;
`

const ReissueOption = styled.div`
    display: flex;
    gap: 0.6em;
    align-items: flex-start;
    margin-top: 1.2em;
    padding-top: 1.2em;
    border-top: 1px solid ${(props) => props.theme.colors.border};
    line-height: 1.4em;
`

const deletionPossibleFromDate = new Date(2020, 9, 3)

// `text` is a getter so it is translated when read, not when this module loads.
export const uploadItemStatuMap = {
    completed: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.completed')
        },
        style: 'success',
        icon: faCheckCircle,
    },
    error: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.failed')
        },
        style: 'error',
        icon: faUndoAlt,
    },
    expired: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.failed')
        },
        style: 'error',
        icon: faUndoAlt,
    },
    generating_error: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.generatingError')
        },
        style: 'error',
        icon: faUndoAlt,
    },
    pending: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.pending')
        },
        style: 'processing',
        icon: faSpinner,
    },
    deleted: {
        get text() {
            return i18n.t('venue.bookingCodes.uploadPage.status.deleted')
        },
        style: 'info',
        icon: faFileTimes,
    },
}

interface UploadPageProps {
    accountSlug: string
    uploadType: UploadType
    uploadsHistoryList: UploadDetails[]
    exampleCSVHref: string
    exampleFileName: string
    loading: boolean
    bookingCodesService: BookingCodesService
    backofficeEndpoint: string
    loginService: LoginService
    onUpload: (file: File) => Promise<UploadDetails>
    reloadUploadsHistoryList: () => void
    onUploadError: () => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    hasFeature: (feature: string, accountSlug?: string) => boolean
    loadData?: () => void
    count?: number
    sort?: Sorting
}

function UploadPage(props: UploadPageProps) {
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
    const [deleteItemId, setDeleteItemId] = useState<string>('')
    const [deleteItemName, setDeleteItemName] = useState<string>('')
    const [deleteItemCount, setDeleteItemCount] = useState<number>(0)
    const [warningText, setWarningText] = useState<string | ReactNode>('')
    // Barcode imports get their warning built at render time instead of stored
    // as JSX, because it carries a checkbox the user can toggle.
    const [preview, setPreview] = useState<UsedBarcodesCount | null>(null)
    const [reissue, setReissue] = useState<boolean>(false)
    const [loadingModal, setLoadingModal] = useState<boolean>(false)
    const user = useAppSelector((state) => state.auth.user)
    const { t } = useTranslation()

    const onDeleteRequest = async (id: string, fileName: string, itemCount: number) => {
        setDeleteItemId(id)
        setDeleteItemName(fileName)
        setDeleteItemCount(itemCount)
        setPreview(null)
        setReissue(false)
        setWarningText(t('venue.bookingCodes.uploadPage.deleteGenericMessage', { fileName }))
        setShowDeleteDialog(true)
        if (props.uploadType === 'barcodes') {
            setPreview(await getBarcodePreview(id))
        }
    }

    const closeDeleteDialog = () => {
        setShowDeleteDialog(false)
        setPreview(null)
        setReissue(false)
    }

    const onConfirmDelete = async () => {
        try {
            setLoadingModal(true)
            await props.bookingCodesService.deleteUploadedCodes(
                props.accountSlug,
                props.uploadType,
                deleteItemId,
                // The count the dialog showed goes back with the request, so if
                // a sale landed in between the server refuses rather than
                // emailing more people than were named here.
                canReissue && reissue ? { expectedOrderCount: preview!.ordersToEmailCount } : undefined,
            )
            props.reloadUploadsHistoryList()
            props.replaceMessages(
                'success',
                'success',
                canReissue && reissue
                    ? t('venue.bookingCodes.uploadPage.deleteSuccessNotified', { count: preview!.ordersToEmailCount })
                    : t('venue.bookingCodes.uploadPage.deleteSuccess'),
            )
            closeDeleteDialog()
            setLoadingModal(false)
            await delay(5000)
            props.removeAllMessages()
        } catch (error) {
            closeDeleteDialog()
            setLoadingModal(false)
            // The server's refusal messages (stale count, over the cap) are
            // written to be read; fall back to the generic one only if there
            // is nothing to show.
            props.replaceMessages(
                'unknown_error',
                'error',
                (error as Error)?.message || t('venue.bookingCodes.genericError'),
            )
        }
    }

    const getBarcodePreview = async (id: string) => {
        try {
            setLoadingModal(true)
            return await props.bookingCodesService.getUsedBarcodesCount(props.accountSlug, id)
        } catch {
            props.replaceMessages('unknown_error', 'error', t('venue.bookingCodes.genericError'))
            setShowDeleteDialog(false)
            return null
        } finally {
            setLoadingModal(false)
        }
    }

    const soldCount = preview?.usedBarcodesCount ?? 0
    const emailCount = preview?.ordersToEmailCount ?? 0
    // Reissue is offered only when there is someone to tell. A sold code whose
    // order is spent - redeemed, or a day that has passed - is left alone and
    // is not counted here, because nothing is taken from a customer who is not
    // emailed about it.
    const canReissue = !!preview && emailCount > 0 && !preview.overLimit

    const barcodeDeleteWarning = (
        <>
            <div>{t('venue.bookingCodes.uploadPage.barcodes.willRemoveUnused', { fileName: deleteItemName })}</div>
            {soldCount > 0 && (
                <>
                    <div style={{ marginTop: '.7em' }}>
                        <Trans
                            i18nKey="venue.bookingCodes.uploadPage.barcodes.soldNote"
                            values={{ soldCount, itemCount: deleteItemCount }}
                            components={{ strong: <strong /> }}
                        />
                    </div>
                    <div>
                        {t('venue.bookingCodes.uploadPage.barcodes.soldCodesStay', {
                            count: Math.max(deleteItemCount - soldCount, 0),
                        })}
                    </div>
                    <div style={{ marginTop: '.7em' }}>
                        {t('venue.bookingCodes.uploadPage.barcodes.reuploadWarning')}
                    </div>
                </>
            )}
            {!!preview && soldCount === 0 && (
                <div style={{ marginTop: '.7em' }}>{t('venue.bookingCodes.uploadPage.barcodes.noneSold')}</div>
            )}
            {canReissue && (
                <ReissueOption>
                    <Checkbox
                        checked={reissue}
                        onChange={(e) => setReissue(e.target.checked)}
                        id="reissue-sold-codes"
                    />
                    <label htmlFor="reissue-sold-codes">
                        <strong>{t('venue.bookingCodes.uploadPage.barcodes.reissueLabel')}</strong>
                        <div>
                            <Trans
                                i18nKey="venue.bookingCodes.uploadPage.barcodes.reissueDescription"
                                count={emailCount}
                                values={{ count: emailCount }}
                                components={{ strong: <strong /> }}
                            />
                        </div>
                    </label>
                </ReissueOption>
            )}
            {!!preview?.overLimit && (
                <div style={{ marginTop: '.7em' }}>
                    {t('venue.bookingCodes.uploadPage.barcodes.overLimit', { count: preview.resendOrderLimit })}
                </div>
            )}
        </>
    )

    const downloadLink = (id: string) => {
        const type = props.uploadType === 'barcodes' ? 'barcodes' : 'discounts'
        return `${props.backofficeEndpoint}dashboard_api/codes/export?account_slug=${props.accountSlug}&import_id=${id}&codes_type=${type}`
    }

    const columns = useMemo(
        () =>
            [
                {
                    accessor: 'filename',
                    Header: t('venue.bookingCodes.uploadPage.columns.fileName'),
                    Cell: ({ row: { original }, value }) => (
                        <DownloadLink
                            loginService={props.loginService}
                            downloadEndpoint={downloadLink(original.uploadId)}
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            <FileNameAsLink title={value}>{original.filename}</FileNameAsLink>
                        </DownloadLink>
                    ),
                },
                {
                    accessor: 'createdBy',
                    Header: t('venue.bookingCodes.uploadPage.columns.uploadedBy'),
                    width: '10em',
                    Cell: ({ value }) => {
                        if (!value) {
                            return '-'
                        }

                        if (conviousEmail(value || '') && !user?.isAdmin) {
                            return 'Convious'
                        }

                        return value
                    },
                },
                {
                    accessor: 'createdAt',
                    Header: t('venue.bookingCodes.uploadPage.columns.uploadedDate'),
                    Cell: ({ value }) => formatISOString(value, `${DateFormats.LONG_DATE} ${DateFormats.SHORT_TIME}`),
                    width: '11.5em',
                },
                {
                    accessor: 'codeCount',
                    Header: t('venue.bookingCodes.uploadPage.columns.count'),
                    align: 'center',
                    disableSortBy: true,
                    Cell: ({ row: { original } }) => {
                        return addSeparators(original.codeCount || original.barcodeCount)
                    },
                    width: '7em',
                },
                {
                    accessor: 'status',
                    Header: t('venue.bookingCodes.uploadPage.columns.status'),
                    align: 'left',
                    disableSortBy: true,
                    Cell: ({ row: { original } }) => {
                        return (
                            <Text status={uploadItemStatuMap[original.status].style as StatusType}>
                                <FontAwesomeIcon
                                    icon={uploadItemStatuMap[original.status].icon}
                                    style={{ marginRight: '.5em' }}
                                    className={original.status === 'pending' ? 'fa-pulse' : ''}
                                />
                                {uploadItemStatuMap[original.status].text}
                            </Text>
                        )
                    },
                    width: '19em',
                },
                {
                    accessor: 'id',
                    width: '4em',
                    disableSortBy: true,
                    Cell: ({ row: { original } }) => {
                        const itemCount = original.codeCount || original.barcodeCount
                        const showDeleteIcon =
                            original.status === 'completed' &&
                            props.uploadType !== 'sold_tickets' &&
                            (props.uploadType === 'barcodes' ||
                                parseISODate(original.createdAt) > deletionPossibleFromDate)

                        if (showDeleteIcon) {
                            return (
                                <FontAwesomeIcon
                                    onClick={() => onDeleteRequest(original.uploadId, original.filename, itemCount)}
                                    icon={faTrash}
                                    style={{ fontSize: '1.1em', cursor: 'pointer' }}
                                />
                            )
                        }

                        return null
                    },
                },
            ] as TableColumn<UploadDetails>[],
        [],
    )
    return (
        <>
            {showDeleteDialog && (
                <ConfirmationDialog
                    title={t('venue.bookingCodes.uploadPage.deleteConfirmTitle')}
                    text={props.uploadType === 'barcodes' ? barcodeDeleteWarning : warningText}
                    buttonText={
                        canReissue && reissue ? t('venue.bookingCodes.uploadPage.deleteAndNotify') : t('common.delete')
                    }
                    destructive
                    onCancel={closeDeleteDialog}
                    onConfirm={onConfirmDelete}
                    loading={loadingModal}
                />
            )}
            <div style={{ margin: '1rem 1.25rem 1.5rem 1.25rem' }}>
                <Row>
                    <Col span={6} data-userpilot={`upload-${props.uploadType}`}>
                        <UploadInput
                            type="file"
                            acceptedTypesMsg="CSV"
                            acceptedTypes="text/csv"
                            extensionCheckOnly
                            onUpload={props.onUpload}
                            handleUploadSuccessResponse={props.reloadUploadsHistoryList}
                            handleUploadError={props.onUploadError}
                            style={{ height: '5em' }}
                            uploadArea
                            inputId="upload"
                            fileSizeLimit={16}
                            parseErrorMessage
                            replaceTopMessages={props.replaceMessages}
                            removeAllMessages={props.removeAllMessages}
                        />
                    </Col>
                    <Col span={6}>
                        <DownloadElement
                            exampleCSVHref={props.exampleCSVHref}
                            exampleFileName={props.exampleFileName}
                        />
                    </Col>
                </Row>
            </div>
            <ReactTable
                loading={props.loading}
                elevation={false}
                noResultsRow
                pagination
                data={props.uploadsHistoryList}
                columns={columns}
                size="small"
                loadData={props.loadData}
                totalCount={props.count}
                sort={props.sort}
            />
        </>
    )
}

export default withFeatures(UploadPage)
